import { Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import * as moment from 'moment';
import { combineLatest, map, takeUntil } from 'rxjs';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { MedicationService } from './../../../../core/services/medication.service';
import { PatientPrescriptionService } from './../../../../core/services/patient-prescription.service';
import { PatientService } from './../../../../core/services/patient.service';
import { MedicationAutocompleteComponent } from './../../../../shared/components/medication-autocomplete/medication-autocomplete.component';
import { UserService } from 'src/app/core/services/user.service';
import { SignatureService, SignatureContext } from 'src/app/core/services/signature.service';
import { GroupService } from 'src/app/core/services/group.service';
import { isStateModeGroup } from 'src/app/shared/util/state-mode.util';
import { FileDownloadService } from 'src/app/core/services/file-download.service';

@Component({
  selector: 'app-prescription-list',
  templateUrl: './prescription-list.component.html',
  styleUrls: ['./prescription-list.component.scss']
})
export class PrescriptionListComponent extends BaseComponent implements OnInit {
  @ViewChildren(MatMenuTrigger) menuTriggers!: QueryList<MatMenuTrigger>;
  @Input() initialExpanded?: boolean;
  expanded?: boolean;
  @Output() expandedChange = new EventEmitter<boolean>();

  @ViewChild('medicationAutocomplete') medicationAutocomplete?: MedicationAutocompleteComponent;

  public expandedHeader: string = 'active';

  prescriptions!: any[] | null;
  activeMedication!: any[] | null;
  inactiveMedication!: any[] | null;
  patient: any;

  sendingDetails = { name: '', email: '' };



  userProfile: any;
  signatureContext!: SignatureContext;
  selectedDoctor: any = {};
  includeSignatureOnPdf: boolean = false;
  includeSignatureOnEmail: boolean = false;

  /** A state unit (tasks/state-mode.md) emails no prescriptions. */
  get stateUnit(): boolean {
    return isStateModeGroup(this.groupService.group);
  }

  constructor(
    private router: Router,
    private patientService: PatientService,
    private patientPrescriptionService: PatientPrescriptionService,
    private medicationService: MedicationService,
    private userService: UserService,
    private signatureService: SignatureService,
    private groupService: GroupService,
    private fileDownloadService: FileDownloadService,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);

    this.patientService.currentPatient$
      .pipe(takeUntil(this.destroy$))
      .subscribe(selectedPatient => {
        if (selectedPatient) {
          this.patient = selectedPatient;
          this.sendingDetails.email = selectedPatient.email || '';
        } else {
          this.patient = null;
          this.sendingDetails = { name: '', email: '' };
        }
      });

    this.userProfile = this.userService.user;
  }

  ngOnInit(): void {
    this.loadMedicationData();
    this.loadSignatureContext();
  }

  private loadSignatureContext(): void {
    // Get initial signature context
    this.signatureContext = this.signatureService.getSignatureContext();

    // Subscribe to user profile changes to update signature context
    this.userService.userProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.signatureContext = this.signatureService.getSignatureContext();
      });

    // Subscribe to group users changes to update signature context
    this.groupService.groupUsers$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.signatureContext = this.signatureService.getSignatureContext();
      });
  }

  onRecipientSelected(recipient: { name: string, email: string }) {
    this.sendingDetails.name = recipient.name;
    this.sendingDetails.email = recipient.email;
  }

  toggleExpanded() {
    this.expanded = !this.expanded;
    this.expandedChange.emit(this.expanded);
  }

  toggleHeader(item: string) {
    this.expandedHeader = this.expandedHeader === item ? '' : item;
  }

  loadMedicationData(): void {
    combineLatest({
      prescriptions: this.patientPrescriptionService.patientPrescriptions$,
      medication: this.patientPrescriptionService.patientMedication$,
    }).pipe(
      map(({ prescriptions, medication }) => {
        return this.sortData(prescriptions || [], medication || []);
      }),
      takeUntil(this.destroy$),
    ).subscribe({
      next: (combinedData) => {
        this.prescriptions = combinedData[0];
        this.activeMedication = combinedData[1];
        this.inactiveMedication = combinedData[2];
      },
      error: (err) => {
        this.prescriptions = [];
        this.activeMedication = [];
        this.inactiveMedication = [];
      }
    });
  }

  sortData(prescriptions: any, medication: any): any[] {


    const grouped = _.groupBy(prescriptions, item => item.date ? moment(item.date, 'DD/MM/YYYY').format('YYYY-MM-DD') : moment(item.createdAt).format('YYYY-MM-DD'));


    const finalPrescriptions = _.chain(grouped)
      .map((value, key) => ({ date: key, items: _.orderBy(value, item => moment(item.date, 'DD/MM/YYYY').format('YYYY-MM-DD'), 'desc') }))
      .orderBy(group => group.date, 'desc')
      .value();

    const activeMedication = medication.filter(((i: any) => i.active))
    const inactiveMedication = medication.filter(((i: any) => !i.active))

    return [finalPrescriptions, activeMedication, inactiveMedication];
  }

  getMedicationString(medication: any) {
    return medication.map((med: any, index: number) => {
      if (med.id) {
        return `${index + 1}. ${med.name || med.medication}: ${med.dose} ${med.unit} ${med.frequency} for ${med.duration}`;
      } else {
        return `${index + 1}. ${med.name || med.medication}: ${med.instructions}`;
      }
    }).join('\n')
  }

  isDate(value: any): boolean {
    return !isNaN(Date.parse(value));
  }

  toggleMedicationActive(medication: any) {
    medication.active = !medication.active;
    const sub = this.patientPrescriptionService.updateMedication(medication.id, medication)
      .subscribe(result => {
        sub.unsubscribe();
      })
  }

  deleteMedication(medication: any) {
    medication.deleted = true
    const sub = this.patientPrescriptionService.updateMedication(medication.id, medication)
      .subscribe(result => {
        sub.unsubscribe();
      })
  }

  async emailPrescription(prescription: any) {
    const patientName = `${this.patient.name}_${this.patient.surname}`;
    const includeSignature = this.signatureService.shouldIncludeSignature(this.includeSignatureOnEmail);

    await this.medicationService.emailExistingPrescription(
      this.patient,
      this.patient.groupId,
      prescription,
      `${patientName}_${moment().valueOf()}_prescription.pdf`,
      this.sendingDetails.email,
      '',
      includeSignature
    );

    this.sendingDetails = { name: '', email: '' };
    this.closeAllMenus(this.menuTriggers);
    this.showToastMessage('Prescription sent successfully.');
  }

  async createNewPrescriptionFromActive(medication: any) {
    if (this.medicationAutocomplete) {
      this.medicationAutocomplete.bindMedicationList(medication);
      const saved = await this.medicationAutocomplete.downloadPrescription();
      if (saved) {
        this.downloadPrescription(saved);
        this.showToastMessage('Prescription created successfully.');
        this.medicationAutocomplete?.resetState();
      }
    }
  }

  async downloadPrescription(prescription: any) {
    const patientName = `${this.patient.name}_${this.patient.surname}`;
    const fileName = `${patientName}_${moment().valueOf()}_prescription.pdf`;
    const includeSignature = this.signatureService.shouldIncludeSignature(this.includeSignatureOnPdf);

    const pdfBytes = await this.medicationService.createVerifiedPrescriptionPdf(prescription, includeSignature);

    // Create a blob from the PDF bytes and trigger the download
    const blob = new Blob([pdfBytes as any], { type: 'application/pdf' });
    await this.fileDownloadService.download(blob, fileName);
    this.closeAllMenus(this.menuTriggers);
  }

  clonePrescriptionMedication(prescription: any) {
    if (this.patientService.isRecordingLocked()) {
      return;
    }
    const medication = [...prescription].map((item: any) => {
      if (item.medicationId) {
        item.id = item.medicationId;
        delete item.medicationId;
      }
      return item;
    })
    this.patientPrescriptionService.setCloning(medication);
    this.router.navigate(['/patients', {
      outlets: {
        patient: ['add-prescription'],
      }
    }]);

  }

  // Note: handleMenuKeydown is no longer needed since SignatureOptionsComponent handles this internally

  // Getter methods still needed for template conditional logic
  get canSign(): boolean {
    return this.signatureContext?.canSign || false;
  }

  get isPhysician(): boolean {
    return this.signatureContext?.isPhysician || false;
  }

  get hasSignaturePermission(): boolean {
    return this.signatureContext?.hasSignaturePermission || false;
  }

  get groupUsers(): any[] {
    return this.signatureContext?.groupUsers || [];
  }

}
